import { useId, useRef, useState, type ReactNode } from 'react';
import type { AvatarKind } from '@circles/convex';
import { AvatarHuePicker, AvatarPicker } from './AvatarPicker';
import { avatarTint } from './avatarTint';
import { AVATAR_ACCEPT, avatarErrorMessage, uploadAvatar, type AvatarUploader } from './avatarUpload';
import c from './AvatarControls.module.css';

/**
 * Choose / change / remove a member's picture (§14.79): an illustrated preset from the library,
 * or a photo, and the background color behind it. The preview (a `MemberAvatar` bound to the live member) and the button styles come
 * from the caller, so the builder's Edit-member modal, the launcher's settings sheet and the join
 * page each keep their own look. A real file input sits behind the labelled photo button; success
 * shows through the caller's live query, plus a short status line.
 */
export function AvatarControls({
  name,
  avatarKind,
  preset,
  hue,
  preview,
  uploader,
  setPreset,
  setHue,
  remove,
  buttonClassName,
  dangerClassName,
  pickerClassName,
  disabled = false,
  uploadLabel = 'Upload photo',
  pickerOpen: initiallyOpen = false,
}: {
  name: string;
  avatarKind: AvatarKind;
  /** The picked preset's id when `avatarKind` is `preset`. */
  preset: string | null;
  /** The member's `avatarHue`, painted behind each picker tile. */
  hue: number;
  preview: ReactNode;
  uploader: AvatarUploader;
  setPreset: (preset: string) => Promise<unknown>;
  setHue: (hue: number) => Promise<unknown>;
  remove: () => Promise<unknown>;
  buttonClassName: string | undefined;
  dangerClassName: string | undefined;
  pickerClassName?: string | undefined;
  disabled?: boolean;
  uploadLabel?: string;
  pickerOpen?: boolean;
}) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [pickerOpen, setPickerOpen] = useState(initiallyOpen);
  const [picked, setPicked] = useState<string | null>(null);
  const [pickedHue, setPickedHue] = useState<number | null>(null);
  const [busy, setBusy] = useState<'upload' | 'remove' | 'preset' | 'hue' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const hasPhoto = avatarKind === 'photo';

  async function act(kind: 'upload' | 'remove' | 'preset' | 'hue', fn: () => Promise<unknown>, done: string) {
    setBusy(kind);
    setError(null);
    setStatus(null);
    try {
      await fn();
      setStatus(done);
    } catch (err) {
      setError(avatarErrorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className={c.root} data-testid="avatar-controls">
      <div className={c.head}>
        {preview}
        <div className={c.side}>
          <div className={c.actions}>
            {!initiallyOpen && (
              <button
                type="button"
                className={buttonClassName}
                aria-expanded={pickerOpen}
                disabled={disabled}
                onClick={() => setPickerOpen((open) => !open)}
              >
                {pickerOpen ? 'Hide pictures' : 'Choose a picture'}
              </button>
            )}
            <button
              type="button"
              className={buttonClassName}
              disabled={disabled || busy !== null}
              onClick={() => inputRef.current?.click()}
            >
              {busy === 'upload' ? 'Uploading…' : hasPhoto ? 'Change photo' : uploadLabel}
            </button>
            {avatarKind !== 'auto' && (
              <button
                type="button"
                className={dangerClassName}
                disabled={disabled || busy !== null}
                onClick={() => void act('remove', remove, hasPhoto ? 'Photo removed.' : 'Picture removed.')}
              >
                {busy === 'remove' ? 'Removing…' : hasPhoto ? 'Remove photo' : 'Remove picture'}
              </button>
            )}
          </div>
          <input
            ref={inputRef}
            id={inputId}
            className={c.input}
            type="file"
            accept={AVATAR_ACCEPT}
            aria-label={`Photo for ${name}`}
            tabIndex={-1}
            disabled={disabled || busy !== null}
            data-testid="avatar-input"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) void act('upload', () => uploadAvatar(file, uploader), 'Photo saved.');
            }}
          />
          <div className={c.message}>
            {error !== null && (
              <p className={c.error} role="alert">
                {error}
              </p>
            )}
            {status !== null && error === null && (
              <p className={c.status} role="status">
                {status}
              </p>
            )}
          </div>
        </div>
      </div>
      {pickerOpen && (
        <>
          <AvatarHuePicker
            name={name}
            value={pickedHue ?? hue}
            disabled={disabled || busy === 'upload' || busy === 'remove'}
            onChange={(h) => {
              setPickedHue(h);
              void act('hue', () => setHue(h), 'Color saved.').then(() => setPickedHue((p) => (p === h ? null : p)));
            }}
          />
          <AvatarPicker
            name={name}
            value={picked ?? preset}
            background={avatarTint(pickedHue ?? hue)}
            disabled={disabled || busy === 'upload' || busy === 'remove'}
            className={pickerClassName}
            onChange={(id) => {
              setPicked(id);
              void act('preset', () => setPreset(id), 'Picture saved.').then(() => setPicked((p) => (p === id ? null : p)));
            }}
          />
        </>
      )}
    </div>
  );
}
